import React, { useState } from "react";
import { HeroVideoSliceFragment } from "../../types/graphql";
import { Button } from "./hero-video.styled";
import { PrismicRichText } from "@prismicio/react";
import { track } from "../../utils/analytics";
import SalesforceContactForm from "../salesforce-contact-form/salesforce-contact-form";
import SalesforceFormContext from "../../utils/contexts/salesforce-form-context";
import { useSalesforceViewContext } from "../../utils/contexts/salesforce-form-context";
import HeroVimeo from "./hero-vimeo";
import clsx from "clsx";
import { GatsbyImage } from "gatsby-plugin-image";
import scrollHashLink from "../../utils/scroll-hash-link";
import * as FA from "../../styles/fontawesome";

interface IHeroVideoProps {
  hero: HeroVideoSliceFragment;
}

const HeroType = {
  VIMEO: "Vimeo Player",
  VIDEO: "Background Video",
};

const HeroVideo = (props: IHeroVideoProps) => {
  return (
    <SalesforceFormContext>
      <HeroVideoContext {...props} />
    </SalesforceFormContext>
  );
};

const HeroVideoContext = (props: IHeroVideoProps) => {
  var hero = props.hero.primary;
  const setFormViewState = useSalesforceViewContext();

  // Hero type
  const heroType = hero?.hero_type || HeroType.VIDEO;

  // Vimeo details
  const vimeoEmbedUrl = hero?.vimeo?.embed_url || "";

  // Video details
  const video = hero?.video || null;
  const videoPoster = hero?.video_poster || null;

  // Text and image details
  const title = hero?.title || "";
  const subtitle = hero?.subtitle || null;
  const image = hero?.image || null;

  // CTA details
  const ctaLink = hero?.cta_link || null;
  const ctaText = hero?.cta_text || "";

  // Salesforce form details
  const salesforceLeadSource = hero?.salesforce_lead_source || "";
  const salesforceFormDisclaimer = hero?.salesforce_form_disclaimer || null;

  // State for showing audio player
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);

  // Audio file and description
  const audioFile = props.hero.primary.audio_file;
  const audioDescription = props.hero.primary.audio_description;

  // Render based on hero type
  if (heroType === HeroType.VIMEO) {
    return hero && vimeoEmbedUrl ? <HeroVimeo {...props} /> : <></>;
  } else {
    return hero && videoPoster?.gatsbyImageData ? (
      <div
        className={clsx([
          "relative bg-xom-dark-blue-100 overflow-hidden",
          hero?.css_class,
        ])}
      >
        <SalesforceContactForm
          leadSource={salesforceLeadSource}
          disclaimer={salesforceFormDisclaimer?.richText}
        >
          <div className="slice-section pt-18">
            {/* Hero Background Video Poster */}
            {videoPoster?.url && (
              <img
                className="absolute top-0 left-0 right-0"
                src={videoPoster?.url}
              />
            )}
            {/* Hero Background Video */}
            <video
              className={clsx([
                "absolute w-[500%] max-w-none -top-[20%] -left-[250%]",
                "lg:-left-[10%] lg:-top-[20%] lg:w-[120%]",
                "xl:-left-[1%] xl:-top-[20%] xl:w-[102%]",
              ])}
              autoPlay
              loop
              muted
            >
              <source src={video?.url || ""} type="video/mp4" />
            </video>
            {/* Hero Background Overlay */}
            <div
              className={clsx([
                "absolute top-0 left-0 right-0",
                "w-[100%] h-[100%]",
                "bg-[linear-gradient(180deg,rgba(9,44,71,0.9)80%,rgba(9,44,71,1.0)100%)]",
              ])}
            />
            <div className={clsx(["relative container"])}>
              {/* Hero Content */}
              <div className="lg:max-w-[calc((7/12)*100%)] !grid gap-16 pb-16">
                {/* Hero Text */}
                <div
                  className={clsx([
                    "!grid gap-8",
                    "justify-items-center lg:justify-items-start",
                  ])}
                >
                  {title && (
                    <h1
                      className={clsx([
                        "text-white !mb-0",
                        "text-center lg:text-left",
                      ])}
                    >
                      {title}
                    </h1>
                  )}
                  {image && image?.gatsbyImageData && (
                    <div className="hero-image lg:hidden max-w-[200px]">
                      <GatsbyImage
                        image={image?.gatsbyImageData}
                        alt={image?.alt || "Xometry Resources"}
                        loading="lazy"
                        className="[&_img]:!object-contain w-full [&>div]:!max-w-none"
                      />
                    </div>
                  )}
                  {subtitle && (
                    <div className="leading-relaxed text-xom-dark-blue-5">
                      <PrismicRichText
                        field={subtitle?.richText}
                        components={{
                          paragraph: ({ children }) => (
                            <p
                              className={clsx([
                                "text-sm lg:text-xl",
                                "text-center lg:text-left",
                                "leading-relaxed text-xom-dark-blue-5",
                              ])}
                            >
                              {children}
                            </p>
                          ),
                        }}
                      />
                    </div>
                  )}
                </div>
                {/* Hero CTAs */}
                <div className="flex flex-col lg:flex-row lg:items-center gap-8">
                  <div className="cta text-center lg:text-left">
                    {ctaText && ctaLink?.url && salesforceLeadSource ? (
                      <Button
                        kind="primary"
                        scale="large"
                        pageSection="hero-video"
                        ctaText={ctaText}
                        onClickFunction={() => {
                          setFormViewState.setCurrentView("form");
                          track("Enterprise Schedule a Consulation", {
                            text: "Enterprise Lead Form Clicked",
                          });
                        }}
                        className="!text-xl !rounded !px-8 !py-4"
                      />
                    ) : ctaText && ctaLink?.url ? (
                      <a
                        href={ctaLink?.url}
                        target={ctaLink?.target || "_self"}
                        className={clsx([
                          "button default rounded",
                          "py-4 px-8",
                          "text-base lg:text-lg",
                        ])}
                        onClick={(
                          e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
                        ) => {
                          scrollHashLink(e, ctaLink?.url);
                        }}
                      >
                        {ctaText}
                      </a>
                    ) : (
                      <></>
                    )}
                  </div>
                  {audioFile && audioFile.url && (
                    <div className="self-center">
                      {showAudioPlayer ? (
                        <audio src={audioFile.url} controls autoPlay />
                      ) : (
                        <button
                          className="flex items-center text-white gap-2.5 cursor-pointer hover:underline"
                          onClick={() => setShowAudioPlayer(true)}
                        >
                          <i className="text-2xl" css={FA.HeadphonesRegular} />
                          <span className="text-left max-w-[150px]">
                            {audioDescription}
                          </span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {/* Hero Image for large screens */}
              {image && image?.gatsbyImageData && (
                <div className="hero-image hidden lg:block lg:absolute lg:-right-[10%] lg:-top-[10%] lg:w-[680px]">
                  <GatsbyImage
                    image={image?.gatsbyImageData}
                    alt={image?.alt || "Xometry Resources"}
                    loading="lazy"
                    className="[&_img]:!object-contain w-full [&>div]:!max-w-none"
                  />
                </div>
              )}
            </div>
          </div>
        </SalesforceContactForm>
      </div>
    ) : (
      <></>
    );
  }
};
export default HeroVideo;
